import { useState, useEffect, useRef } from 'react'
import data from '@emoji-mart/data'
import Picker from '@emoji-mart/react'
import { Smile } from 'lucide-react'
import useThemeStore from '../../stores/themeStore'

const BioEditor = ({
  bio = '',
  onChange,
  maxLength = 200,
  showToggle = true,
  showOnProfile = true,
  onToggleVisibility
}) => {
  const [localBio, setLocalBio] = useState(bio)
  const [showPicker, setShowPicker] = useState(false)
  const textareaRef = useRef(null)
  const pickerRef = useRef(null)
  const { effectiveTheme } = useThemeStore()
  const remaining = maxLength - (localBio?.length || 0)

  useEffect(() => {
    setLocalBio(bio || '')
  }, [bio])

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false)
      }
    }

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showPicker])

  const handleChange = (e) => {
    const value = e.target.value.slice(0, maxLength)
    setLocalBio(value)
    onChange(value)
  }

  const handleEmojiSelect = (emoji) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const newValue = localBio.slice(0, start) + emoji.native + localBio.slice(end)

    if (newValue.length <= maxLength) {
      setLocalBio(newValue)
      onChange(newValue)

      // Restore cursor position after emoji
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + emoji.native.length, start + emoji.native.length)
      }, 0)
    }

    setShowPicker(false)
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Bio</label>
        {showToggle && (
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnProfile}
              onChange={(e) => onToggleVisibility(e.target.checked)}
              className="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
            />
            Show on profile
          </label>
        )}
      </div>

      <div className="relative">
        <textarea
          ref={textareaRef}
          value={localBio}
          onChange={handleChange}
          placeholder="Tell us about yourself..."
          rows={3}
          className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors"
        />

        {/* Emoji picker button */}
        <button
          type="button"
          onClick={() => setShowPicker(!showPicker)}
          className="absolute right-2 top-2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition-colors"
          title="Add emoji"
        >
          <Smile className="w-5 h-5" />
        </button>

        {/* Emoji picker dropdown */}
        {showPicker && (
          <div ref={pickerRef} className="absolute right-0 top-full mt-2 z-50">
            <Picker
              data={data}
              onEmojiSelect={handleEmojiSelect}
              theme={effectiveTheme}
              previewPosition="none"
              skinTonePosition="search"
              emojiSize={20}
              emojiButtonSize={28}
              perLine={8}
              maxFrequentRows={2}
            />
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row sm:justify-between gap-1 sm:gap-0 text-xs">
        <span className="text-gray-500 dark:text-gray-400">Short bio for your public profile</span>
        <span
          className={
            remaining < 20
              ? 'text-warning-500'
              : remaining < 0
                ? 'text-error-500'
                : 'text-gray-500 dark:text-gray-400'
          }
        >
          {remaining} characters remaining
        </span>
      </div>
    </div>
  )
}

export default BioEditor
